import { useEffect, useState } from 'react';

import {
    RangeSlider,
    RangeSliderMark,
    RangeSliderThumb,
    RangeSliderTrack,
    RangeSliderFilledTrack,
    Tooltip,
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from '@hooks/store';
import { MAXIMUM_MENTOR_EXPERIENCE } from '@modules/find-mentor/constants';
import {
    setFindMentorFilters,
    initialState as initialMentorState,
} from '@modules/find-mentor/slice';

type CustomSliderMarkProps = {
    value: number;
};

const CustomSliderMark = ({ value }: CustomSliderMarkProps) => {
    const labelStyles = {
        mt: '2',
        fontSize: 'sm',
    };
    let ml = '-1.5';
    if (value === 0) {
        ml = '-1';
    } else if (value === MAXIMUM_MENTOR_EXPERIENCE) {
        ml = '-3.5';
    }
    return (
        <RangeSliderMark value={value} {...labelStyles} ml={ml}>
            {value === MAXIMUM_MENTOR_EXPERIENCE ? 'Max' : value}
        </RangeSliderMark>
    );
};

const YearsOfExperienceSlider = () => {
    const [showMinTooltip, setShowMinTooltip] = useState(false);
    const [showMaxTooltip, setShowMaxTooltip] = useState(false);
    const mentorState = useAppSelector(state => state.reducer.mentorFilters);
    const [{ totalExperienceMin, totalExperienceMax }, setMentorExperience] = useState({
        totalExperienceMin: mentorState.totalExperienceMin,
        totalExperienceMax: mentorState.totalExperienceMax,
    });
    useEffect(() => {
        setMentorExperience({
            totalExperienceMin: mentorState.totalExperienceMin,
            totalExperienceMax: mentorState.totalExperienceMax,
        });
    }, [mentorState.totalExperienceMin, mentorState.totalExperienceMax]);
    const dispatch = useAppDispatch();
    return (
        <RangeSlider
            sx={{ width: '90%', marginLeft: '10px' }}
            id="years-of-experience-slider"
            defaultValue={[mentorState.totalExperienceMin, mentorState.totalExperienceMax]}
            min={initialMentorState.totalExperienceMin}
            max={initialMentorState.totalExperienceMax}
            value={[totalExperienceMin, totalExperienceMax]}
            onChange={([min, max]) => {
                setMentorExperience({
                    totalExperienceMin: min,
                    totalExperienceMax: max,
                });
            }}
            onChangeEnd={([min, max]) => {
                dispatch(
                    setFindMentorFilters({
                        totalExperienceMin: min,
                        totalExperienceMax: max,
                    })
                );
            }}
        >
            {[0, 5, 10, 15].map((val, index) => (
                <CustomSliderMark key={`key${index + val}`} value={val} />
            ))}
            <RangeSliderTrack>
                <RangeSliderFilledTrack bgColor="primary.light" />
            </RangeSliderTrack>
            <Tooltip
                hasArrow
                color="white"
                backgroundColor="primary.light"
                placement="top"
                isOpen={showMinTooltip}
                label={`${totalExperienceMin}`}
            >
                <RangeSliderThumb
                    index={0}
                    onMouseEnter={() => setShowMinTooltip(true)}
                    onMouseLeave={() => setShowMinTooltip(false)}
                />
            </Tooltip>
            <Tooltip
                hasArrow
                backgroundColor="primary.light"
                color="white"
                placement="top"
                isOpen={showMaxTooltip}
                label={`${
                    totalExperienceMax === MAXIMUM_MENTOR_EXPERIENCE ? 'Max' : totalExperienceMax
                }`}
            >
                <RangeSliderThumb
                    index={1}
                    onMouseEnter={() => setShowMaxTooltip(true)}
                    onMouseLeave={() => setShowMaxTooltip(false)}
                />
            </Tooltip>
        </RangeSlider>
    );
};

export default YearsOfExperienceSlider;
